<template>
  <div>
    <div class="d-flex justify-content-end align-items-center mt-2 mb-2">
      <b-button variant="primary" size="md" @click="onCreate">{{
        $t('Add New Return Fee')
      }}</b-button>
    </div>

    <vue-good-table
      mode="remote"
      :sort-options="{ enabled: false }"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      :pagination-options="{
        perPage: filter.limit,
        setCurrentPage: filter.page,
        enabled: true,
        jumpFirstOrLast: true,
        rowsPerPageLabel: t('Rows per page'),
        ofLabel: t('ofLabel'),
        nextLabel: t('nextLabel'),
        prevLabel: t('prevLabel'),
        dropdownAllowAll: false
      }"
      :show-footer="true"
      :rows="rows"
      :columns="getColumns"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <table-no-result-found slot="emptystate" />
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'providerName'">
          {{ props.row.providerName }}
        </span>
        <span v-if="props.column.field === 'price'">
          {{ props.row.price || '-' }}
        </span>
        <span v-if="props.column.field === 'actions'">
          <span class="d-flex justify-content-center table-icon-buttons">
            <b-button
              :id="`edit-return-fee-${props.row.id}`"
              variant="primary"
              type="button"
              size="sm"
              @click="onEdit(props.row)"
            >
              <feather-icon icon="Edit3Icon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`edit-return-fee-${props.row.id}`"
                :title="t('Edit')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>

            <b-button
              :id="`delete-return-fee-${props.row.id}`"
              variant="danger"
              type="button"
              size="sm"
              @click="onDelete(props.row)"
            >
              <feather-icon icon="XIcon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`delete-return-fee-${props.row.id}`"
                :title="t('Delete')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>
          </span>
        </span>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { BButton, BTooltip } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';

export default {
  name: 'CostPricesSenderPrices',
  components: {
    VueGoodTable,
    BButton,
    BTooltip
  },
  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      }
    };
  },

  computed: {
    getColumns() {
      return [
        { label: this.$t('Provider'), field: 'providerName' },
        { label: this.$t('Rate'), field: 'price' },
        { label: this.$t('Actions'), field: 'actions', sortable: false }
      ];
    }
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      this.isLoading = true;
      useService
        .getAllCustomerReturnFees()
        .then(response => {
          const list = Array.isArray(response.data) ? response.data : [];
          this.rows = list.map(item => ({
            id: item.id,
            customerId: item.customer && item.customer.id,
            providerId: item.provider && item.provider.id,
            providerName: item.provider && item.provider.name,
            price: item.price
          }));
          this.totalRecords = this.rows.length;
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onCreate() {
      this.$router.push({ name: 'cost-prices-consignor-prices-create' });
    },

    onEdit(row) {
      this.$router.push({
        name: 'cost-prices-consignor-prices-detail',
        params: { providerId: row.providerId }
      });
    },

    onDelete(row) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Customer Parameter Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService
            .deleteCustomerReturnFee(row.providerId, row.id)
            .then(response => {
              this.showSuccessMessage(`${response.message}`);
              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    }
  },

  created() {
    this.loadItems();
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
